/**
 * The agent panel's title row: Casey standing on its bottom edge and "Ask
 * Casey" on the left; widen / narrow, new thread, the conversations menu
 * and close on the right. Under it, for an existing thread,
 * one line on who started it and who has joined.
 */
import { Dialog as SheetPrimitive } from "radix-ui";
import {
  ChevronsLeft,
  ChevronsRight,
  Maximize2,
  Minimize2,
  Plus,
  X
} from "lucide-react";
import type { FC, ReactNode } from "react";

import { AgentPeek } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  JoinedStack,
  PersonAvatar
} from "@/features/caseIde/chat/components/ThreadMenuItem";
import type { TeamMember } from "@/features/caseIde/chat/mentions";

/** Who started the open thread, how long ago, and who joined since. */
export type ThreadProvenance = {
  starter: Pick<TeamMember, "label" | "avatarUrl"> | null;
  /** "you" for the signed-in person. */
  starterLabel: string;
  age: string;
  joined: TeamMember[];
};

type Props = {
  /** The agent's name, for the header's and close button's accessible names. */
  label: string;
  /** Starts a new thread; shows the New Thread button. */
  onNew?: () => void;
  /** The conversations menu (thread switcher), if the chat is mounted. */
  menu?: ReactNode;
  /** Jumps the panel between its narrow and wide widths; shows Widen / Narrow. */
  onToggleWidth?: () => void;
  /** The panel is at its wide width (the button narrows it). */
  wide?: boolean;
  /** Fills the page with the chat (or restores it); shows Expand Chat / Restore Chat Size. */
  onToggleExpand?: () => void;
  /** The chat fills the page (the button restores it). */
  expanded?: boolean;
  /** The open thread's starter line; omitted on a new thread. */
  provenance?: ThreadProvenance | null;
  /** What the chat is about, quieter after the title ("Client", a client's name). */
  subtitle?: string | null;
  /**
   * Render the sheet's close button. On by default for the agent sheet; hosts
   * that mount this header inline (e.g. the Requests IDE pane) pass `false`,
   * as there is no `Sheet`/`Dialog` ancestor to close.
   */
  showClose?: boolean;
};

export const panelDescription = (label: string): string =>
  `Talk to ${label} about any client, case or deadline.`;

const HeaderButton: FC<{
  label: string;
  testId?: string;
  onClick?: () => void;
  children: ReactNode;
}> = ({ label, testId, onClick, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={label}
        data-testid={testId}
        onClick={onClick}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom">{label}</TooltipContent>
  </Tooltip>
);

/**
 * The chat's Expand control, styled as the case IDE's pane Maximize button
 * (`pane-expand`, pressed while expanded) so every Casey chat expands alike.
 */
const ExpandButton: FC<{ expanded: boolean; onClick: () => void }> = ({
  expanded,
  onClick
}) => {
  const label = expanded ? "Restore Chat Size" : "Expand Chat";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-xs"
          className="pane-expand text-fg-3 hover:text-fg"
          aria-label={label}
          aria-pressed={expanded}
          data-testid="agent-panel-expand"
          onClick={onClick}
        >
          {expanded ? <Minimize2 /> : <Maximize2 />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="flex items-center gap-2">
        {label}
        {expanded ? <Kbd>Esc</Kbd> : null}
      </TooltipContent>
    </Tooltip>
  );
};

const ProvenanceLine: FC<{ provenance: ThreadProvenance }> = ({
  provenance: { starter, starterLabel, age, joined }
}) => (
  <div
    className="flex shrink-0 items-center gap-1.5 px-4 pt-2 pb-1 text-sm"
    data-testid="agent-panel-provenance"
  >
    {starter ? <PersonAvatar member={starter} /> : null}
    <span className="min-w-0 truncate text-fg-3">
      Started by <span className="text-fg-2">{starterLabel}</span>
      {age ? ` · ${age}` : ""}
    </span>
    <span className="flex-1" />
    {joined.length ? (
      <>
        <JoinedStack joined={joined} cutout="ring-surface" />
        <span className="whitespace-nowrap text-fg-3">{`${joined.length} joined`}</span>
      </>
    ) : null}
  </div>
);

export const AgentPanelHeader: FC<Props> = ({
  label,
  onNew,
  menu,
  onToggleWidth,
  wide = false,
  onToggleExpand,
  expanded = false,
  provenance,
  subtitle,
  showClose = true
}) => (
  <>
    <header
      aria-label={label}
      data-testid="agent-panel-header"
      className="flex h-11 shrink-0 items-center gap-0.5 border-b border-line-2 pr-2 pl-3"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 self-stretch">
        <span className="agent-peek-stage self-end [--peek-pop-delay:180ms]">
          <AgentPeek className="h-8" />
        </span>
        <span
          className="truncate text-sm font-semibold text-fg"
          data-testid="agent-panel-title"
        >
          Ask Casey
        </span>
        {subtitle ? (
          <span
            className="min-w-0 truncate text-sm text-fg-3"
            data-testid="agent-panel-subtitle"
          >
            {subtitle}
          </span>
        ) : null}
      </div>
      {onToggleWidth && !expanded ? (
        <HeaderButton
          label={wide ? "Narrow" : "Widen"}
          testId="agent-panel-width"
          onClick={onToggleWidth}
        >
          {wide ? <ChevronsRight /> : <ChevronsLeft />}
        </HeaderButton>
      ) : null}
      {onToggleExpand ? (
        <ExpandButton expanded={expanded} onClick={onToggleExpand} />
      ) : null}
      {onNew ? (
        <HeaderButton
          label="New Thread"
          testId="agent-panel-new-thread"
          onClick={onNew}
        >
          <Plus />
        </HeaderButton>
      ) : null}
      {menu}
      {showClose ? (
        <SheetPrimitive.Close asChild>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`Close ${label}`}
            data-testid="agent-panel-close"
          >
            <X />
          </Button>
        </SheetPrimitive.Close>
      ) : null}
    </header>
    {provenance ? <ProvenanceLine provenance={provenance} /> : null}
  </>
);
